import { useEffect, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { Switch } from '@/components/ui/switch.jsx'
import { Button } from '@/components/ui/button.jsx'
import { useConsent } from '@/context/ConsentContext.jsx'

const COOKIE_POLICY_URL = '/politica-de-cookies-visualg-web.pdf'

/**
 * Central de preferências de cookies (Política de Cookies §8 + guia de cookies da ANPD):
 * o usuário lê cada categoria e liga/desliga as não essenciais. Categorias essenciais
 * permanecem sempre ativas. Análise de uso e publicidade têm escolhas independentes.
 */
export function CookiePreferencesModal({ onClose, onCloseAutoFocus }) {
  const { consent, savePreferences, setPreferencesOpen } = useConsent()
  const [analyticsOn, setAnalyticsOn] = useState(consent.analytics === 'granted')
  const [advertisingOn, setAdvertisingOn] = useState(consent.advertising === 'granted')
  const [open, setOpen] = useState(true)

  useEffect(() => {
    setPreferencesOpen?.(open)
    return () => setPreferencesOpen?.(false)
  }, [open, setPreferencesOpen])

  const close = () => {
    setOpen(false)
    onClose?.()
  }

  const apply = (analyticsValue, advertisingValue) => {
    savePreferences({ analytics: analyticsValue ? 'granted' : 'denied', advertising: advertisingValue ? 'granted' : 'denied' })
    close()
  }

  return (
    <Dialog.Root open={open} onOpenChange={nextOpen => { if (!nextOpen) close() }}>
      <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-[140] bg-black/55 backdrop-blur-sm" />
      <Dialog.Content onCloseAutoFocus={onCloseAutoFocus} className="fixed left-1/2 top-1/2 z-[141] flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-2xl">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border p-5">
          <div>
            <Dialog.Title className="text-lg font-semibold">Preferências de cookies</Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-muted-foreground">
              Gerencie as categorias de cookies não essenciais. Saiba mais na{' '}
              <a
                href={COOKIE_POLICY_URL}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-primary underline underline-offset-4"
              >
                Política de Cookies
              </a>
              .
            </Dialog.Description>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Fechar preferências de cookies"
            className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 max-h-[55vh] space-y-4 overflow-y-auto p-5">
          <section className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-sm font-semibold">Cookies essenciais</h3>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs font-semibold text-emerald-400">Sempre ativo</span>
                <Switch
                  checked
                  disabled
                  aria-label="Cookies essenciais (sempre ativos)"
                  className="data-[state=checked]:bg-emerald-500"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Necessários para login, segurança, manutenção de sessão e funcionamento da
              plataforma. Sempre ativos — não podem ser desligados.
            </p>
          </section>

          <section className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-sm font-semibold">Análise de uso (Clarity e Google Analytics)</h3>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`text-xs font-semibold ${analyticsOn ? 'text-emerald-400' : 'text-slate-400'}`}
                >
                  {analyticsOn ? 'Ativado' : 'Desativado'}
                </span>
                <Switch
                  checked={analyticsOn}
                  onCheckedChange={setAnalyticsOn}
                  aria-label="Análise de uso (Clarity e Google Analytics)"
                  className="border border-slate-500 data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-600"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Clarity e Google Analytics ajudam a entender o uso e melhorar a experiência. A gravação só ocorre com
              o seu consentimento e apenas para usuários adultos logados — menores de idade
              nunca são gravados, e o conteúdo sensível (código, conversas, pagamento) é
              mascarado.
            </p>
          </section>
          <section className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-sm font-semibold">Cookies de publicidade (Google e Adsterra)</h3>
              <Switch checked={advertisingOn} onCheckedChange={setAdvertisingOn} aria-label="Cookies de publicidade (Google e Adsterra)" className="border border-slate-500 data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-600" />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Ao autorizar, você permite cookies de publicidade e anúncios personalizados do Google. Sem autorização, o Google pode exibir anúncios limitados; Adsterra e anúncios automáticos do AdSense exigem autorização. Menores identificados recebem somente anúncios Google sem personalização. Não enviamos seu código, conversas ou dados do perfil para segmentação. Assinantes não recebem anúncios. Recusar mantém as funções gratuitas disponíveis.</p>
          </section>
        </div>
        <div className="flex shrink-0 flex-col gap-2 border-t border-border p-5 sm:flex-row sm:justify-end">
          <Button variant="outline" size="sm" onClick={() => apply(false, false)}>
            Rejeitar não essenciais
          </Button>
          <Button variant="outline" size="sm" onClick={() => apply(analyticsOn, advertisingOn)}>
            Salvar preferências
          </Button>
          <Button size="sm" onClick={() => apply(true, true)}>
            Aceitar todos
          </Button>
        </div>
      </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
